import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import {
  ElencoRichiesteNuovoInvioResponse,
  type ValiditaDocumentoRichiestaRes,
  type ValiditaDocumentoRichiesteResponse,
} from '../models/interfaces/documenti-adempimenti/elenco-richieste-nuovo-invio.interface';
import {
  buildRicercaRichiesteNuovoInvioQueryString,
  type RicercaRichiesteNuovoInvioParams,
} from '../models/interfaces/documenti-adempimenti/filtri-ricerca-richieste-nuovo-invio.interface';
import type {
  DestinatariNuovoInvioGetResI,
  RichiediNuovoInvioPostReqI,
  RitrasmettiRichiestaNuovoInvioPostReqI,
} from '../models/interfaces/documenti-adempimenti/richiedi-nuovo-invio.interface';
import type { ResRiepilogoDettaglioNuovoInvioI } from '../models/interfaces/documenti-adempimenti/riepilogo-dettaglio-richiesta-nuovo-invio.interface';
import type { StoricoRichiesteReinvioResponse } from '../models/interfaces/documenti-adempimenti/storico-richieste-reinvio.interface';
import { HttpClientConte } from './http-client-conte';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RichiesteNuovoInvioService {
  private readonly httpClientConte = inject(HttpClientConte);
  private readonly endpoint = `${environment.CONTEXT_DOCUMENTAZIONE}/richieste-nuovo-invio`;

  /**
   * Lista richieste nuovo invio (tab Richieste nuovo invio).
   */
  getElencoRichieste(
    page: number = 0,
    size: number = 10,
    filtri?: RicercaRichiesteNuovoInvioParams,
  ): Observable<ElencoRichiesteNuovoInvioResponse | null> {
    const filtriQueryString = filtri ? buildRicercaRichiesteNuovoInvioQueryString(filtri) : '';
    const url = `${this.endpoint}?page=${page}&size=${size}${filtriQueryString ? `&${filtriQueryString}` : ''}`;

    return this.httpClientConte.httpGet<ElencoRichiesteNuovoInvioResponse>(url).pipe(
      catchError((error) => {
        console.error('[RichiesteNuovoInvioService] Error fetching elenco richieste:', error);
        return of(null);
      }),
    );
  }

  /** Destinatari + PEC ente per il form di richiesta nuovo invio. */
  getDestinatari(sequDocumento: number): Observable<DestinatariNuovoInvioGetResI> {
    const url = `${this.endpoint}/destinatari?sequDocumento=${sequDocumento}`;
    return this.httpClientConte.httpGet<DestinatariNuovoInvioGetResI>(url).pipe(
      catchError((error) => {
        console.error('[RichiesteNuovoInvioService] Error fetching destinatari:', error);
        return of({ emailPecEnte: '', destinatari: [] });
      }),
    );
  }

  /** Crea una richiesta di nuovo invio. */
  createRichiesta(payload: RichiediNuovoInvioPostReqI): Observable<ResRiepilogoDettaglioNuovoInvioI> {
    return this.httpClientConte.httpPost<ResRiepilogoDettaglioNuovoInvioI>(this.endpoint, payload);
  }

  /** Dettaglio riepilogo di una richiesta nuovo invio. */
  getRiepilogoDettaglio(sequRichiesta: string | number): Observable<ResRiepilogoDettaglioNuovoInvioI | null> {
    const url = `${this.endpoint}/${sequRichiesta}`;
    return this.httpClientConte.httpGet<ResRiepilogoDettaglioNuovoInvioI>(url).pipe(
      catchError((error) => {
        console.error('[RichiesteNuovoInvioService] Error fetching riepilogo dettaglio:', error);
        return of(null);
      }),
    );
  }

  /**
   * POST che indica se il documento collegato è ancora valido
   * (adempimento / esercizio attivi) per ritrasmissione e carica documento.
   */
  postValiditaDocumento(listaSequDocumento: number[]): Observable<ValiditaDocumentoRichiestaRes[]> {
    const url = `${this.endpoint}/validita-documento`;

    return this.httpClientConte
      .httpPost<ValiditaDocumentoRichiesteResponse | ValiditaDocumentoRichiestaRes[]>(url, { listaSequDocumento })
      .pipe(
        map((response) => {
          if (Array.isArray(response)) {
            return response;
          }
          const data = response?.data;
          if (Array.isArray(data)) {
            return data;
          }
          return [];
        }),
        catchError((error) => {
          console.error('[RichiesteNuovoInvioService] Error fetching validita documento:', error);
          return of([] as ValiditaDocumentoRichiestaRes[]);
        }),
      );
  }

  /**
   * Evade la richiesta caricando un nuovo documento.
   * Multipart: `request` (JSON) + `file`, come POST /documenti-adempimenti.
   * sequRichiesta è nel path; il body non invia sequTipoDocumento / sequEsercizio.
   * La risposta è l'id del documento creato.
   */
  evadiRichiesta(sequRichiesta: string | number, payload: FormData): Observable<number> {
    return this.httpClientConte.httpPost<number>(`${this.endpoint}/${sequRichiesta}/evadi`, payload);
  }

  /** Ritrasmette una richiesta di nuovo invio. */
  ritrasmettiRichiesta(
    sequRichiesta: string | number,
    payload: RitrasmettiRichiestaNuovoInvioPostReqI,
  ): Observable<ResRiepilogoDettaglioNuovoInvioI> {
    return this.httpClientConte.httpPost<ResRiepilogoDettaglioNuovoInvioI>(
      `${this.endpoint}/${sequRichiesta}/ritrasmissione`,
      payload,
    );
  }

  /**
   * Storico ritrasmissioni: GET .../richieste-nuovo-invio/{sequRichiesta}/ritrasmissioni
   * (oggi senza page/size). Se torna la paginazione BE: aggiungere `?page=&size=`
   * come in `getElencoRichieste`.
   */
  getStoricoRichieste(
    sequRichiesta: string | number,
  ): Observable<StoricoRichiesteReinvioResponse | null> {
    const url = `${this.endpoint}/${sequRichiesta}/ritrasmissioni`;
    return this.httpClientConte.httpGet<StoricoRichiesteReinvioResponse>(url).pipe(
      catchError((error) => {
        console.error('[RichiesteNuovoInvioService] Error fetching storico richieste:', error);
        return of(null);
      }),
    );
  }
}
